import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Sticky status banner rendered at the top of every supplier-portal
// page. Three states (green/amber/red) per supplier compliance state.
// Clickable anywhere – navigates to /portal/compliance.
//
// Props:
//   tone: 'green' | 'amber' | 'red'
//   message: string ("All compliance documents up to date" etc)
//   cta: string ("Manage" | "Resolve")
//
// On the /portal/compliance page itself the stripe still renders but
// doesn't navigate anywhere on click – the supplier is already there.
//
// position:sticky pinned to top:0 with a z-index that sits above
// page content. Inline style rather than Tailwind because the static
// background colour binding (var(--fusion-red) etc) is clearer this way.
const TONE_STYLES = {
  // The compliance traffic-light is the ONE sanctioned amber in the
  // product (build #12 decision flag): the guide copy literally names
  // "Green / Amber / Red", so the dots must match their labels. Amber
  // is removed everywhere else, but retained here.
  green: { bg: '#92D400', fg: '#131313' },
  amber: { bg: '#E89A2E', fg: '#131313' },
  red:   { bg: '#E0290A', fg: '#F9FCF1' },
};

export default function ComplianceStripe({ tone = 'green', message, cta }) {
  const navigate = useNavigate();
  const location = useLocation();
  const onCompliancePage = location.pathname === '/portal/compliance';
  const style = TONE_STYLES[tone] || TONE_STYLES.green;

  const handleClick = () => {
    if (onCompliancePage) return;
    navigate('/portal/compliance');
  };

  return (
    <div
      role={onCompliancePage ? undefined : 'button'}
      tabIndex={onCompliancePage ? -1 : 0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (onCompliancePage) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); }
      }}
      className="sticky top-3 z-40 mx-4 sm:mx-6 rounded-xl shadow-[0_10px_28px_-12px_rgba(0,0,0,0.4)] flex items-center justify-between px-4 sm:px-6 py-3 font-['Metropolis'] text-[11px] font-bold uppercase select-none transition-[filter] duration-150 hover:brightness-110"
      style={{ backgroundColor: style.bg, color: style.fg, letterSpacing: '0.15em', cursor: onCompliancePage ? 'default' : 'pointer' }}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <span
          aria-hidden="true"
          className="inline-block w-2 h-2 rounded-full opacity-85 flex-shrink-0"
          style={{ backgroundColor: style.fg }}
        />
        <span className="truncate">{message}</span>
      </div>
      {!onCompliancePage && (
        // Build #18 §5: was a solid-white "Resolve" PILL that read as a
        // second heavyweight button (stacking with the compliance card's
        // red CTA). Downgraded to a lightweight underlined link so the bar
        // stays a persistent reminder and the card is the one primary red.
        <span
          className="flex-shrink-0 ml-3 inline-flex items-center gap-1 text-[11px] font-extrabold tracking-[0.1em] underline underline-offset-2 opacity-90"
          aria-hidden="true">
          {cta} →
        </span>
      )}
    </div>
  );
}
